// src/usuarios/Organigrama.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";

// =========================
// Helpers: Auth headers
// =========================
const authOnly = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// =========================
// Helpers: construir árbol (gerente_id)
// =========================
const construirArbol = (usuarios, gerenteId = null) => {
  return (usuarios || [])
    .filter((u) => (u?.gerente_id ?? null) === gerenteId)
    .map((u) => ({
      ...u,
      children: construirArbol(usuarios, u.id),
    }));
};

// =========================
// Helpers: Avatar (mismo criterio que Gestión de Usuarios)
// =========================
const getAvatarUrl = (usuario) => {
  if (!usuario?.avatar_url) return null;
  return `${Global.url}${usuario.avatar_url}`;
};

const getIniciales = (usuario) => {
  const nombre = (usuario?.nombre || "").trim();
  const apellidos = (usuario?.apellidos || "").trim();
  const n1 = nombre ? nombre.split(" ")[0]?.[0] || "" : "";
  const a1 = apellidos ? apellidos.split(" ")[0]?.[0] || "" : "";
  const combo = (n1 + a1).toUpperCase();
  if (combo) return combo;
  if (nombre) return nombre[0].toUpperCase();
  return "?";
};

// =========================
// UI: Nodo (recursivo)
// =========================
const Nodo = ({ nodo, depth = 0 }) => {
  const avatarSrc = getAvatarUrl(nodo);
  const hasChildren = Array.isArray(nodo?.children) && nodo.children.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
      }}
    >
      {/* Card del nodo */}
      <Paper
        sx={{
          p: 1.25,
          backgroundColor: "#ffffff",
          borderRadius: 2,
          minWidth: 260,
          maxWidth: 360,
        }}
        title={nodo?.email || ""}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
          <Avatar src={avatarSrc || undefined} sx={{ width: 40, height: 40 }}>
            {!avatarSrc ? getIniciales(nodo) : null}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }} noWrap>
              {`${nodo?.nombre || ""} ${nodo?.apellidos || ""}`.trim() || "—"}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 0.25, flexWrap: "wrap" }}>
              <Chip label={nodo?.rol || "—"} variant="filled" />
              {nodo?.email ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                  {nodo.email}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Conector vertical hacia hijos */}
      {hasChildren ? (
        <Box
          sx={{
            width: 2,
            height: 18,
            backgroundColor: "rgba(0,0,0,0.18)",
            borderRadius: 2,
          }}
        />
      ) : null}

      {/* Hijos */}
      {hasChildren ? (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            alignItems: "flex-start",
            justifyContent: "center",
            flexWrap: "wrap",
            position: "relative",
            pt: 0.5,
          }}
        >
          {/* “barra” horizontal (visual) */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: "rgba(0,0,0,0.18)",
              borderRadius: 2,
              mx: 2,
            }}
          />

          {nodo.children.map((child) => (
            <Box
              key={child.id}
              sx={{
                position: "relative",
                pt: 2, // espacio para el conector hacia arriba
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* conector desde la barra horizontal hacia el nodo hijo */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  width: 2,
                  height: 16,
                  backgroundColor: "rgba(0,0,0,0.18)",
                  borderRadius: 2,
                }}
              />
              <Nodo nodo={child} depth={depth + 1} />
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export const Organigrama = () => {
  const toast = useToast();

  // =========================
  // State
  // =========================
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================
  // Load
  // =========================
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${Global.url}usuarios/organigrama`, { headers: authOnly() });
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          toast.error?.("Error al cargar organigrama.");
          setUsuarios([]);
          return;
        }

        setUsuarios(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error al obtener organigrama:", err);
        toast.error?.("No se pudo conectar con el servidor.");
        setUsuarios([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // Derivados
  // =========================
  const arbol = useMemo(() => construirArbol(usuarios), [usuarios]);

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        {/* Header */}
        <Box className="pageHeader">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Organigrama de la empresa
          </Typography>
          <Typography className="pageHeader__subtitle">
            Jerarquía por manager (gerente_id) y rol de usuario.
          </Typography>
        </Box>

        {/* Content */}
        <Box className="contentContainer contentContainer--full">
          <Paper sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1 }}>
                <CircularProgress size={20} />
                <Typography sx={{ color: "text.secondary" }}>Cargando…</Typography>
              </Box>
            ) : arbol.length === 0 ? (
              <Typography sx={{ color: "text.secondary", fontStyle: "italic", p: 1 }}>
                No hay datos de organigrama para mostrar.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 920, py: 1 }}>
                  <Stack spacing={3} sx={{ alignItems: "center" }}>
                    {arbol.map((n) => (
                      <Nodo key={n.id} nodo={n} />
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export default Organigrama;
